import React from "react";
import { Badge, OverlayTrigger, Popover } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { Invoice2 } from "src/docuclipper/DocuclipperTypes";
import {
  checkInvoiceConsistency,
  formatDelta,
  InvoiceConsistency,
} from "./invoiceConsistency";

type Props = {
  invoice: Pick<Invoice2, "total" | "subTotal" | "tax" | "lines"> | null | undefined;
};

function buildDetails(c: InvoiceConsistency): React.ReactNode {
  const items: string[] = [];
  if (c.headerMismatch) {
    items.push(
      `Subtotal + Tax (${(c.subTotal + c.tax).toFixed(2)}) ≠ Total (${c.total.toFixed(
        2
      )}). Δ ${formatDelta(c.headerDelta)}.`
    );
  }
  if (c.linesMismatch) {
    items.push(
      `Line items sum (${c.linesSum.toFixed(2)}) ≠ Subtotal (${c.subTotal.toFixed(
        2
      )}). Δ ${formatDelta(c.linesDelta)}.`
    );
  }
  return items.length > 0 ? (
    <ul className="mb-0 pl-3">
      {items.map((text, i) => (
        <li key={i}>{text}</li>
      ))}
    </ul>
  ) : (
    <span>Subtotal + Tax equals Total, and line items sum to Subtotal.</span>
  );
}

export const InvoiceConsistencyBadge: React.FC<Props> = ({ invoice }) => {
  const c = checkInvoiceConsistency(invoice);
  if (!c.isApplicable) return null;

  const consistent = c.isConsistent;
  const popover = (
    <Popover id="invoice-consistency-popover">
      <Popover.Title>
        {consistent ? "Invoice consistent" : "Numbers don't add up"}
      </Popover.Title>
      <Popover.Content>{buildDetails(c)}</Popover.Content>
    </Popover>
  );

  return (
    <OverlayTrigger placement="bottom" overlay={popover}>
      <Badge
        variant={consistent ? "success" : "warning"}
        className="p-1 pr-2"
        data-cy={
          consistent
            ? "invoice-consistency-ok"
            : "invoice-consistency-warning"
        }
        style={{ cursor: "help" }}
      >
        <FontAwesomeIcon
          color="white"
          icon={consistent ? faCheck : faExclamationTriangle}
        />
        <span className="ml-2 text-white">
          {consistent ? "Invoice consistent" : "Numbers don't add up — review"}
        </span>
      </Badge>
    </OverlayTrigger>
  );
};

export default InvoiceConsistencyBadge;
